import React, { lazy, Suspense, useEffect } from "react";

import { observer } from "mobx-react-lite";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  Routes,
} from "react-router-dom";

import { AdminPanel } from "src/adminPanel/AdminPanel";
import { AgentPage } from "src/agents/AgentPage";
import { AgentsPage } from "src/agents/AgentsPage";
import { AgentVersionPage } from "src/agents/AgentVersionPage";
import { AgentVersionsPage } from "src/agents/AgentVersionsPage";
import { Flow, FlowVersion } from "src/api/flowTypes";
import { useFlowVersion } from "src/api/flowVersionQueries";
import { baseUrl } from "src/api/hosts";
import {
  useFlow,
  useUserOrganizations,
  useWorkspaces,
  useWorkspace as useWorkspaceV1,
} from "src/api/queries";
import { useWorkspace } from "src/api/workspacesV2Api";
import { LoadingView } from "src/base-components/LoadingView";
import { RoleOverrideIndicator } from "src/base-components/RoleOverrideIndicator";
import { Spinner } from "src/base-components/Spinner";
import { CaseQueueConfigurationPage } from "src/cases/CaseQueueConfigurationPage";
import { CasesPage } from "src/cases/CasesPage";
import { CaseTemplatesPage } from "src/cases/CaseTemplatesPage";
import { CaseTemplateEditPage } from "src/cases/CaseTemplateVersionPage";
import { CaseTemplateVersionsPage } from "src/cases/CaseTemplateVersionsPage";
import { CaseViewPage } from "src/cases/CaseViewPage";
import { useRevision } from "src/changeHistory/queries";
import { SimpleRoleType } from "src/clients/taktile-api";
import { DecisionsOverview } from "src/decisionsOverview/DecisionsOverview";
import { Entities } from "src/entities/Entities";
import { SidepaneProvider } from "src/entities/entityView/DecisionsSidePane/SidePaneContext";
import { EntityViewWrapper } from "src/entities/entityView/EntityView";
import { EntityTabIds } from "src/entities/entityView/useEntityTabRefresh";
import { EventSchemaEditorSidebar } from "src/eventsCatalogue/EventSchemaEditorSidebar";
import { EventsCatalogue } from "src/eventsCatalogue/EventsCatalogue";
import { EventsCatalogueIndex } from "src/eventsCatalogue/EventsCatalogueIndex";
import { EventViewSidebar } from "src/eventsCatalogue/EventViewSidebar";
import { FeaturesPage } from "src/featureCatalogue/Features/FeaturesPage";
import { ManageFeatureQueryPage } from "src/featureCatalogue/Queries/ManageFeatureQueryPage";
import { QueriesPage } from "src/featureCatalogue/Queries/QueriesPage";
import { FlowHistoryContent } from "src/flow/decisionHistory/FlowHistoryContent";
import { FlowPage } from "src/flow/FlowPage";
import { FlowTrafficContent } from "src/flow/FlowTrafficContent";
import { FlowVersionsContent } from "src/flow/FlowVersionsContent";
import { NavigateToFlowVersion } from "src/flowContainer/NavigateToFlowVersion";
import { VersionAuthoringWrapper } from "src/flowContainer/VersionAuthoringWrapper";
import { FlowOverview } from "src/flowsOverview/FlowOverview";
import { GitbookAuthPage } from "src/gitbookAuth/GitbookAuthPage";
import { useIsCaseManagementEnabled } from "src/hooks/useIsCaseManagementEnabled";
import { useWorkspaceFeatureGates } from "src/hooks/useWorkspaceFeatureGates";
import { JobContent } from "src/jobs/JobContent";
import { JobsContent } from "src/jobs/JobsContent";
import { JobRun } from "src/jobs/types";
import { MainPageDashboard } from "src/mainPage/MainPage";
import { ReviewCaseContent } from "src/manualReview/ReviewCaseContent";
import { ReviewCasePreviewContent } from "src/manualReview/ReviewCasePreviewContent";
import { ReviewQueueContent } from "src/manualReview/ReviewQueueContent";
import { OmniboxOther } from "src/omnibox/OmniboxOther";
import { OutcomesContent } from "src/outcomes/OutcomesContent";
import { PerformanceRouter } from "src/performance/PerformanceRouter";
import { DisplayRedirects } from "src/router/DisplayRedirects";
import { OrgFavicon } from "src/router/DynamicFavicon";
import { ErrorPage } from "src/router/ErrorPage";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import { PartnerStorageManager } from "src/router/PartnerStorageManager";
import { PickWorkspace } from "src/router/PickWorkspace";
import { ProtectedPage } from "src/router/ProtectedPage";
import { ProtectedRoute } from "src/router/ProtectedRoute";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { PageViewTracker } from "src/router/Track";
import {
  AuthorPageParams,
  adminPanelPath,
  DashboardPageParams,
  getUrlToSettingsPage,
  getUrlToWsDashboard,
  getWelcomeUrl,
  RevisionPageParams,
  SimulationPageParams,
  WsDashboardPageCode,
} from "src/router/urls";
import { SettingsPage, SettingsSubpages } from "src/settings/SettingsPage";
import { SettingsPageRedirect } from "src/settings/SettingsPageRedirect";
import { canAccessSettingsPage } from "src/settings/utils";
import { useSimulationRun } from "src/simulations/useSimulationRun";
import { useIsUserRefreshed } from "src/store/AuthStore";
import { SimpleRoleProvider } from "src/store/SimpleRoleProvider";
import { WelcomePage } from "src/userManagement/WelcomePage";
import { ErrorBoundary } from "src/utils/ErrorBoundary";
import { useBootTheena } from "src/utils/useBootTheena";
import { useLastWorkspaceId } from "src/utils/useLastWorkspaceId";
import { useParamsDecode } from "src/utils/useParamsDecode";
import { OutgoingWebhooksPage } from "src/webhooks/WebhooksPage";

const DocsPage = lazy(() => import("src/docs/DocsPage"));
const Retool = lazy(() =>
  import("src/retool/Retool").then((module) => ({ default: module.Retool })),
);

enum ORG_ROUTE_CODES {
  UsersPermissions = "users-permissions",
}

const FlowVersionRoutes: React.FC = () => {
  const { workspace, modules } = useWorkspaceContext();
  const { flow_id, version_id, orgId } = useParamsDecode<AuthorPageParams>();

  // Fetching flow and flow version here allows to disable refetchOnMount on all child components
  // but still able fetch latest data on initial page visit while navigating within the app
  // const workspaceQuery = useWorkspace(wsId);
  const flowQuery = useFlow(flow_id, { refetchOnMount: true });
  const versionQuery = useFlowVersion(version_id, true);
  const isRevision = false;

  return (
    <LoadingView
      queryResult={[flowQuery, versionQuery]}
      renderUpdated={([flow, version]: [Flow, FlowVersion]) => (
        <Outlet
          context={{
            workspace,
            modules,
            flow,
            version,
            orgId,
            isRevision,
          }}
        />
      )}
    />
  );
};

const WorkspaceRoutesContext: React.FC = () => {
  const { wsId, orgId } = useParamsDecode<DashboardPageParams>();
  const { data: workspace, isLoading, isError } = useWorkspaceV1(wsId);
  const {
    data: workspaceV2,
    isLoading: isWorkspaceV2Loading,
    isError: isWorkspaceV2Error,
  } = useWorkspace(wsId);

  if (isLoading || isWorkspaceV2Loading) {
    return <Spinner />;
  }

  if (isError || isWorkspaceV2Error) {
    return (
      <ErrorPage message="You do not have access to the requested organization or workspace. Please contact Taktile support if you think this is an error." />
    );
  }

  const modules = workspaceV2?.modules;

  return <Outlet context={{ workspace, modules, orgId }} />;
};

const FlowVersionRevisionRoutes: React.FC = () => {
  const { workspace, modules, orgId } = useWorkspaceContext();
  const { flow_id, version_id, revision_etag } =
    useParamsDecode<RevisionPageParams>();

  // Fetching flow and flow version here allows to disable refetchOnMount on all child components
  // but still able fetch latest data on initial page visit while navigating within the app
  const flowQuery = useFlow(flow_id, { refetchOnMount: true });
  const versionQuery = useRevision({
    versionId: version_id,
    etag: revision_etag,
  });
  const isRevision = true;

  return (
    <LoadingView
      queryResult={[flowQuery, versionQuery]}
      renderUpdated={([flow, version]: [Flow, FlowVersion]) => (
        <Outlet
          context={{
            workspace,
            modules,
            flow,
            version,
            orgId,
            isRevision,
          }}
        />
      )}
    />
  );
};

const SimulationVersionRoutes: React.FC = () => {
  const { workspace, modules, orgId } = useWorkspaceContext();
  const { flow_id, version_id, simulationId } =
    useParamsDecode<SimulationPageParams>();

  // Fetching flow and flow version here allows to disable refetchOnMount on all child components
  // but still able fetch latest data on initial page visit while navigating within the app
  const flowQuery = useFlow(flow_id, { refetchOnMount: true });
  const versionQuery = useFlowVersion(version_id, true);
  const simulationQuery = useSimulationRun(flow_id, version_id, simulationId);

  const isRevision = false;
  const isSimulation = true;

  return (
    <LoadingView
      queryResult={[flowQuery, versionQuery, simulationQuery]}
      renderErrored={(message) => <ErrorPage message={message} />}
      renderUpdated={([flow, version, _simulation]: [
        Flow,
        FlowVersion,
        JobRun,
      ]) => (
        <Outlet
          context={{
            workspace,
            modules,
            flow,
            version,
            orgId,
            isRevision,
            isSimulation,
            simulationId,
          }}
        />
      )}
    />
  );
};

const FlowRoutes = (
  <Route element={<FlowPage />} path="flows/:flow_id/">
    <Route
      element={
        <ProtectedPage canAccess={(c) => c.flowVersions.canAccess}>
          <FlowVersionsContent />
        </ProtectedPage>
      }
      index
    />

    <Route
      element={
        <ProtectedPage canAccess={(c) => c.flowVersions.canAccess}>
          <FlowTrafficContent />
        </ProtectedPage>
      }
      path="traffic"
    />
    <Route
      element={
        <ProtectedPage canAccess={(c) => c.decisionHistory.canAccess}>
          <FlowHistoryContent />
        </ProtectedPage>
      }
      path="history"
    />
    <Route
      element={
        <ProtectedPage canAccess={(c) => c.outgoingWebhooks.canAccess}>
          <OutgoingWebhooksPage />
        </ProtectedPage>
      }
      path="webhooks"
    />
    <Route
      element={
        <ProtectedPage canAccess={(c) => c.flows.canEdit}>
          <PerformanceRouter />
        </ProtectedPage>
      }
      path="performance"
    />
    <Route
      element={<ProtectedRoute canAccess={(c) => c.manualReview.canAccess} />}
      path="review-queue"
    >
      <Route element={<ReviewQueueContent />} index />
      <Route element={<ReviewCaseContent />} path=":case_id" />
    </Route>
    <Route
      element={<ProtectedRoute canAccess={(c) => c.jobs.canAccess} />}
      path="jobs"
    >
      <Route element={<JobsContent />} index />
      <Route element={<JobContent />} path=":job_id" />
    </Route>
    <Route
      element={<ProtectedRoute canAccess={(c) => c.outcomes.canAccess} />}
      path="outcomes"
    >
      <Route element={<OutcomesContent />} index />
    </Route>
  </Route>
);

const WorkspaceRoutes: React.FC = observer(() => {
  const { wsId, orgId } = useParamsDecode<DashboardPageParams>();
  const [, setLastWorkspaceId] = useLastWorkspaceId();
  const welcomeUrl = getWelcomeUrl(orgId);
  const { workspace } = useWorkspaceContext();
  const featureGates = useWorkspaceFeatureGates();
  const organizations = useUserOrganizations();
  const organization = organizations.data?.find((org) => org.id === orgId);
  const { isEnabled: isCaseManagementEnabled } = useIsCaseManagementEnabled();

  useEffect(() => {
    setLastWorkspaceId(wsId);
  }, [setLastWorkspaceId, wsId]);

  useBootTheena({
    enabled: workspace?.config?.enable_customer_support_widget ?? true,
  });

  if (!workspace || (organizations.data && !organization)) {
    return (
      <Routes>
        <Route
          element={
            <ErrorPage message="You do not have access to the requested organization or workspace. Please contact Taktile support if you think this is an error." />
          }
          path="*"
        />
      </Routes>
    );
  }

  return (
    <SimpleRoleProvider>
      <PartnerStorageManager />
      <OrgFavicon />
      <OmniboxOther />
      <RoleOverrideIndicator />
      <Routes>
        <Route
          element={<Navigate to={getUrlToWsDashboard({ orgId, wsId })} />}
          index
        />

        <Route
          element={<ProtectedRoute canAccess={canAccessSettingsPage} />}
          path="settings"
        >
          <Route
            element={
              <ProtectedPage canAccess={(c) => c.usersPermissions.canAccess}>
                <SettingsPage subpage={SettingsSubpages.Users} />
              </ProtectedPage>
            }
            path="users"
          />
          <Route
            element={
              <ProtectedPage canAccess={(c) => c.connections.canAccess}>
                <SettingsPage subpage={SettingsSubpages.Connections} />
              </ProtectedPage>
            }
            path="connections"
          />
          <Route
            element={
              <ProtectedPage canAccess={(c) => c.apiKeys.canAccess}>
                <SettingsPage subpage={SettingsSubpages.ApiKeys} />
              </ProtectedPage>
            }
            path="api-keys"
          />
          <Route
            element={
              <ProtectedPage canAccess={(c) => c.auditLogs.canAccess}>
                <SettingsPage subpage={SettingsSubpages.AuditLogs} />
              </ProtectedPage>
            }
            path="audit-logs"
          />
          <Route
            element={
              <ProtectedPage canAccess={(c) => c.role === SimpleRoleType.ADMIN}>
                <SettingsPage subpage={SettingsSubpages.Powertools} />
              </ProtectedPage>
            }
            path="powertools"
          />
          <Route element={<SettingsPageRedirect />} index />
        </Route>
        <Route element={<ProtectedRoute redirectPath={welcomeUrl} />}>
          <Route element={<MainPageDashboard />}>
            <Route element={<></>} path={WsDashboardPageCode.Dashboard} />
            <Route
              element={
                <FlowOverview organizationId={orgId} workspaceId={wsId} />
              }
              path={WsDashboardPageCode.Flows}
            />
            {featureGates.entitiesEnabled && (
              <Route path={WsDashboardPageCode.Entities}>
                <Route element={<Entities />} index />
                <Route element={<Entities />} path=":schema" />
                {Object.values(EntityTabIds).map((tab) => (
                  <Route
                    key={tab}
                    element={<EntityViewWrapper tab={tab} />}
                    path={`:schema/:id/${tab}`}
                  />
                ))}
              </Route>
            )}
            {featureGates.featuresEventsEnabled && (
              <Route path={WsDashboardPageCode.Events}>
                <Route element={<EventsCatalogueIndex />} index />
                <Route element={<EventsCatalogue />} path=":event_type">
                  <Route
                    element={
                      <SidepaneProvider>
                        <EventViewSidebar />
                      </SidepaneProvider>
                    }
                    path="event/:event_id"
                  />
                  <Route element={<EventSchemaEditorSidebar />} path="edit" />
                </Route>
              </Route>
            )}
            {featureGates.featuresEventsEnabled && (
              <Route path={WsDashboardPageCode.Features}>
                <Route
                  element={
                    <ProtectedPage
                      canAccess={(c) => c.featureCatalogue.canAccess}
                    >
                      <FeaturesPage />
                    </ProtectedPage>
                  }
                  index
                />
                <Route
                  element={
                    <ProtectedPage
                      canAccess={(c) => c.featureQueriesCatalogue.canAccess}
                    >
                      <QueriesPage />
                    </ProtectedPage>
                  }
                  path="queries"
                />
                <Route
                  element={
                    <ProtectedPage
                      canAccess={(c) => c.featureQueriesCatalogue.canAccess}
                    >
                      <ManageFeatureQueryPage />
                    </ProtectedPage>
                  }
                  path="queries/manage"
                />
              </Route>
            )}
          </Route>
          <Route
            element={
              <ProtectedPage canAccess={(c) => c.decisionHistory.canAccess}>
                <SidepaneProvider>
                  <DecisionsOverview />
                </SidepaneProvider>
              </ProtectedPage>
            }
            path="decisions"
          />
          <Route
            element={<NavigateToUsersPermissions />}
            path={ORG_ROUTE_CODES.UsersPermissions}
          />
          {FlowRoutes}
        </Route>

        {/* Case management routes */}
        <Route
          element={
            <ProtectedRoute
              canAccess={() => isCaseManagementEnabled}
              redirectPath={getUrlToWsDashboard({ orgId, wsId })}
            />
          }
          path="cases"
        >
          <Route path="templates">
            <Route element={<MainPageDashboard />}>
              <Route
                element={
                  <ProtectedPage canAccess={(c) => c.caseTemplates.canAccess}>
                    <CaseTemplatesPage />
                  </ProtectedPage>
                }
                index
              />
              <Route
                element={<CaseTemplateVersionsPage />}
                path=":templateId/versions"
              />
            </Route>
            <Route
              element={
                <ProtectedPage canAccess={(c) => c.caseTemplates.canAccess}>
                  <CaseTemplateEditPage />
                </ProtectedPage>
              }
              path=":templateId/versions/:versionId"
            />
          </Route>

          <Route element={<MainPageDashboard />}>
            <Route
              element={
                <ProtectedPage canAccess={(c) => c.cases.canAccess}>
                  <CasesPage />
                </ProtectedPage>
              }
              index
            />
          </Route>

          <Route
            element={
              <ProtectedPage canAccess={(c) => c.cases.canAccess}>
                <CaseViewPage />
              </ProtectedPage>
            }
            path=":case_id"
          />

          <Route path="queues">
            <Route
              element={
                <ProtectedPage canAccess={(c) => c.caseQueues.canCreate}>
                  <CaseQueueConfigurationPage />
                </ProtectedPage>
              }
              path="new"
            />

            <Route element={<MainPageDashboard />}>
              <Route
                element={
                  <ProtectedPage canAccess={(c) => c.cases.canAccess}>
                    <CasesPage />
                  </ProtectedPage>
                }
                path=":queue_id"
              />
            </Route>

            <Route
              element={
                <ProtectedPage canAccess={(c) => c.caseQueues.canEdit}>
                  <CaseQueueConfigurationPage />
                </ProtectedPage>
              }
              path=":queue_id/edit"
            />
          </Route>
        </Route>
        {isFeatureFlagEnabled(FEATURE_FLAGS.agents) && (
          <Route
            element={
              <ProtectedPage canAccess={(c) => c.agents.canAccess}>
                <MainPageDashboard />
              </ProtectedPage>
            }
            path="agents"
          >
            <Route element={<AgentsPage />} index />
            <Route element={<AgentPage />} path=":agentId">
              <Route element={<AgentVersionsPage />} path="versions" />
            </Route>
            <Route
              element={<AgentVersionPage />}
              path=":agentId/versions/:versionId"
            />
          </Route>
        )}
        <Route element={<NavigateToFlowVersion />} path="flow/:flow_id/" />
        <Route
          element={
            <ProtectedRoute canAccess={(c) => c.flowVersions.canAccess} />
          }
        >
          <Route
            element={<FlowVersionRoutes />}
            path="flow/:flow_id/version/:version_id/"
          >
            <Route element={<VersionAuthoringWrapper />} index />
            <Route
              element={
                <Suspense fallback={<Spinner />}>
                  <DocsPage />
                </Suspense>
              }
              path="docs"
            />
          </Route>

          <Route
            element={<FlowVersionRevisionRoutes />}
            path="flow/:flow_id/version/:version_id/revision/:revision_etag"
          >
            <Route element={<VersionAuthoringWrapper />} index />
          </Route>

          <Route
            element={
              <ProtectedPage canAccess={(c) => c.simulations.canAccess}>
                <SimulationVersionRoutes />
              </ProtectedPage>
            }
            path="flow/:flow_id/version/:version_id/simulation/:simulationId"
          >
            <Route element={<VersionAuthoringWrapper />} index />
          </Route>
        </Route>
        <Route
          element={
            <ProtectedRoute
              canAccess={(c) => c.manualReview.canAccess}
              redirectPath={welcomeUrl}
            />
          }
        >
          {/**
           * This route is outside of DashboardLayout,
           * because we don't want it to have sidebar
           * It is just manual review case preview
           */}
          <Route
            element={<ReviewCasePreviewContent />}
            path="flows/:flow_id/review-queue/preview"
          />

          <Route
            element={
              <ProtectedPage canAccess={(c) => c.manualReview.canAccess}>
                <Suspense fallback={<Spinner />}>
                  <Retool wsId={wsId} />
                </Suspense>
              </ProtectedPage>
            }
            path="retool/:retool_id"
          />
        </Route>

        <Route element={<ErrorPage message="404: Page not found" />} path="*" />
      </Routes>
    </SimpleRoleProvider>
  );
});

const NavigateToUsersPermissions = () => {
  const { orgId } = useParamsDecode<{ orgId: string }>();
  const workspaces = useWorkspaces(true);
  const workspace = workspaces.data?.find((ws) => ws.organization_id === orgId);

  if (!workspace) {
    return <Navigate to={getWelcomeUrl(orgId)} replace />;
  }

  return (
    <Navigate
      to={getUrlToSettingsPage(
        orgId,
        workspace?.id ?? "",
        SettingsSubpages.Users,
      )}
      replace
    />
  );
};

const OrgRoutes = observer(() => {
  const { orgId } = useParamsDecode<{ orgId: string }>();
  const { isLoading, data: organizations } = useUserOrganizations();
  const organization = organizations?.find((org) => org.id === orgId);

  const unauthorizedMessage =
    "You do not have access to the requested organization. Please contact Taktile support if you think this is an error.";

  if ((organizations && !organization) || isLoading) {
    return (
      <Routes>
        <Route
          element={
            isLoading ? (
              <Spinner />
            ) : (
              <ErrorPage message={unauthorizedMessage} />
            )
          }
          path="*"
        />
      </Routes>
    );
  }

  return (
    <SimpleRoleProvider>
      <Routes>
        <Route
          element={
            <ProtectedPage canAccess={(c) => c.usersPermissions.canAccess}>
              <NavigateToUsersPermissions />
            </ProtectedPage>
          }
          path={ORG_ROUTE_CODES.UsersPermissions}
        />
        <Route element={<WelcomePage />} path="welcome" />
        <Route element={<ErrorPage message="404: Page not found" />} path="*" />
      </Routes>
    </SimpleRoleProvider>
  );
});

const RoutesWrapper: React.FC = () => (
  <ErrorBoundary>
    <Outlet />
  </ErrorBoundary>
);

export const Router = () => {
  const refreshed = useIsUserRefreshed();

  if (!refreshed) {
    return <Spinner />;
  }

  const basename = baseUrl();

  const router = createBrowserRouter(
    createRoutesFromElements(
      <>
        <Route element={<RoutesWrapper />}>
          <Route element={<GitbookAuthPage />} path="gitbook-auth" />
          <Route element={<PageViewTracker />}>
            <Route element={<PickWorkspace />} path="" />
            <Route element={<AdminPanel />} path={adminPanelPath} />
            <Route
              element={<WorkspaceRoutesContext />}
              path="org/:orgId/ws/:wsId/*"
            >
              <Route element={<WorkspaceRoutes />} path="*" />
            </Route>
            <Route element={<OrgRoutes />} path="org/:orgId/*" />
            <Route element={<ErrorPage />} path="error" />
            <Route element={<DisplayRedirects />} path="display/:type" />
            <Route
              element={<ErrorPage message="404: Page not found" />}
              path="*"
            />
          </Route>
        </Route>
      </>,
    ),
    {
      basename: basename ? basename + "decide/" : "/decide",
    },
  );

  return <RouterProvider router={router} />;
};
